import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import SublyApi from '../helpers/Api';

/**
 * Custom hook for fetching and managing submission form breakdown statistics
 * Provides real-time data for the PendingSubmissionsCard component
 */
const useSubmissionFormStats = ({ status = 'pending', refreshTrigger = null } = {}) => {
	const [formBreakdown, setFormBreakdown] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [lastFetchTime, setLastFetchTime] = useState(null);
	
	const token = useSelector((state) => state.admin.token);
	const previousRefreshTrigger = useRef(refreshTrigger);

	const fetchFormBreakdown = useCallback(async (forceRefresh = false) => {
		if (!token) {
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const breakdown = await SublyApi.getSubmissionFormBreakdown(token, {
				status,
				forceRefresh
			});

			setFormBreakdown(breakdown || []);
			setLastFetchTime(new Date());
		} catch (err) {
			console.error('Error fetching submission form breakdown:', err);
			setError(err.message || 'Failed to fetch form breakdown');
			// Keep previous data on error to avoid UI flicker
		} finally {
			setLoading(false);
		}
	}, [token, status]);

	// Initial fetch with cache busting
	useEffect(() => {
		fetchFormBreakdown(true); // Force refresh to bypass cache
	}, [fetchFormBreakdown]);

	// Refresh only when trigger actually changes value (not just reference)
	useEffect(() => {
		if (refreshTrigger !== null && 
			previousRefreshTrigger.current !== refreshTrigger && 
			lastFetchTime) {
			fetchFormBreakdown(true); // Force refresh
			previousRefreshTrigger.current = refreshTrigger;
		}
	}, [refreshTrigger, fetchFormBreakdown, lastFetchTime]);

	const refresh = useCallback(() => {
		fetchFormBreakdown(true);
	}, [fetchFormBreakdown]);

	return {
		formBreakdown,
		loading,
		error,
		refresh,
		lastFetchTime
	};
};

export default useSubmissionFormStats;